/**
 * Turning FHIR resources into things a HUMAN can read.
 *
 * Shared by the clinician app and the patient portal. The audiences differ and
 * the wording differs, but the rule below does not, and it is the one thing
 * that must never be decided twice.
 *
 * Pure, so the rules that matter are testable without a DOM.
 *
 * THE RULE THIS MODULE EXISTS FOR is §16.3: "null ≠ 0 ≠ 'not done' ≠
 * 'declined' ≠ 'unknown'. A missing glucose is not a normal glucose."
 *
 * So a value is a discriminated union rather than a string that might be empty.
 * A component cannot accidentally render "—" for a recorded zero, or a blank
 * for a deliberate `dataAbsentReason`, because the three are different shapes
 * and the compiler makes you handle each. A formatting convention would have
 * let one collapse into another the first time someone reached for `?? '—'`.
 *
 * Nothing here computes a clinical value. Derived numbers come from
 * @glycemia/glyco-core (§2.4); this module only formats what it is given.
 *
 * And nothing here KNOWS a code. This file briefly held a table of LOINC
 * constants and CI rejected it: §8.0.1 forbids a hardcoded code list in
 * application code, "not in a constant, not in an enum, not in a switch". The
 * membership sets arrive from lib/codesets.ts, which expands them from the IG
 * at runtime — so widening what counts as a systolic reading is a change in
 * ig/ rather than in a React component nobody would think to look at.
 */
/** Text for a coded concept, preferring what was actually recorded (§8.0.3). */
export const codeText = (concept) => {
    if (concept === undefined)
        return 'Unspecified';
    if (concept.text !== undefined && concept.text !== '')
        return concept.text;
    const coding = concept.coding?.[0];
    return coding?.display ?? coding?.code ?? 'Unspecified';
};
/**
 * Format a quantity with its unit.
 *
 * `unit` is the human display and `code` is the UCUM symbol; they usually
 * differ ("mmHg" against "mm[Hg]"). The display one is shown, and there is no
 * fallback that would render a bare number: §16.1 treats a clinical number
 * without a unit as a bug, and that applies to what reaches the screen.
 */
export const formatQuantity = (quantity) => {
    if (quantity?.value === undefined)
        return { kind: 'missing' };
    const unit = quantity.unit ?? quantity.code;
    return {
        kind: 'value',
        number: quantity.value,
        text: unit === undefined ? String(quantity.value) : `${quantity.value} ${unit}`,
    };
};
/** The value of an observation, honouring dataAbsentReason. */
export const observationValue = (observation) => {
    if (observation.valueQuantity?.value !== undefined) {
        return formatQuantity(observation.valueQuantity);
    }
    if (observation.dataAbsentReason !== undefined) {
        return { kind: 'absent', reason: codeText(observation.dataAbsentReason) };
    }
    return { kind: 'missing' };
};
/** Does this concept carry any code from the given set? */
const inSet = (concept, codes) => (concept?.coding ?? []).some((coding) => coding.code !== undefined && codes.has(coding.code));
/**
 * Read a blood pressure from its panel components.
 *
 * §7.1: "BP profile must carry both systolic and diastolic as components; never
 * two loose Observations." Reading it from components rather than pairing two
 * resources by timestamp is the display-side half of that rule — pairing by
 * time is exactly how a diastolic ends up attached to the wrong reading.
 *
 * A panel missing one component yields `missing` for that half rather than
 * silently rendering half a reading as though it were whole.
 */
export const bloodPressureOf = (observation, codes) => {
    const component = (set) => {
        const match = (observation.component ?? []).find((c) => inSet(c.code, set));
        if (match === undefined)
            return { kind: 'missing' };
        if (match.valueQuantity?.value !== undefined)
            return formatQuantity(match.valueQuantity);
        if (match.dataAbsentReason !== undefined) {
            return { kind: 'absent', reason: codeText(match.dataAbsentReason) };
        }
        return { kind: 'missing' };
    };
    return {
        systolic: component(codes.systolic),
        diastolic: component(codes.diastolic),
        effective: observation.effectiveDateTime,
    };
};
/** Is this observation in the given role's code set? */
export const isInCodeSet = (observation, codes) => inSet(observation.code, codes);
/**
 * Newest first.
 *
 * §16.2 forbids date arithmetic on seconds for clinical meaning; this is a sort
 * over ISO instants, which compares correctly as strings only when they share
 * an offset — so it parses instead. An undated observation sorts last rather
 * than being dropped: it is still part of the record.
 */
export const byRecencyDesc = (items) => [...items].sort((a, b) => {
    const left = a.effectiveDateTime === undefined ? -Infinity : Date.parse(a.effectiveDateTime);
    const right = b.effectiveDateTime === undefined ? -Infinity : Date.parse(b.effectiveDateTime);
    return right - left;
});
/** A condition is active unless its clinicalStatus says otherwise (§7.1). */
export const isActiveCondition = (condition) => {
    const status = condition.clinicalStatus?.coding?.[0]?.code;
    return (status === undefined || status === 'active' || status === 'recurrence' || status === 'relapse');
};
export const isActiveMedication = (request) => request.status === 'active';
/**
 * A one-line dose, from the STRUCTURED fields.
 *
 * `dosageInstruction.text` is a human rendering that §7.1 treats as
 * supplementary; the structured dose is the record. Reading text first would
 * show whatever a sending system happened to write, including for a
 * prescription whose structured dose says something else.
 */
export const doseSummary = (request) => {
    const instruction = request.dosageInstruction?.[0];
    if (instruction === undefined)
        return 'No dose recorded';
    const dose = formatQuantity(instruction.doseAndRate?.[0]?.doseQuantity);
    const frequency = instruction.timing?.repeat?.frequency;
    const period = instruction.timing?.repeat?.periodUnit;
    if (dose.kind !== 'value') {
        // §7.1: a dose belongs in doseAndRate, never only in free text. Saying so
        // is more useful than quietly showing the prose instead.
        return instruction.text === undefined
            ? 'No structured dose recorded'
            : `No structured dose recorded — free text: "${instruction.text}"`;
    }
    const schedule = frequency === undefined || period === undefined
        ? ''
        : ` · ${frequency}× per ${period === 'd' ? 'day' : period}`;
    const route = instruction.route?.text === undefined ? '' : ` · ${instruction.route.text}`;
    return `${dose.text}${schedule}${route}`;
};
//# sourceMappingURL=display.js.map